import { memo, ReactElement, useMemo, useState, useEffect } from 'react';

import { Container } from '@/common/ui/containers/Container/Container';
import { IAccordionTab } from '@/common/ui';
import Paragraph from '@/shared/Paragraph/ui/Paragraph';
import Layout from '@/shared/Layout/ui/Layout';
import { Tomorrow } from '@/shared/images/icons/Tomorrow';

import * as S from './VActivityPage.styles';
import { IReview } from '@/entities/Activity/domain/Review.domain';
import { VActivityPageProps } from './VActivityPage.types';
import { VActivityDetail } from './VActivityDetail/VActivityDetail';
import { VActivitySubtitle } from './VActivitySubtitle/VActivitySubtitle';
import { VActivityTabs } from './VActivityTabs/VActivityTabs';
import { VActivityMeta } from './VActivityMeta/VActivityMeta';
import { VStickyFooter } from './VStickyFooter/VStickyFooter';
import { VActivityFeaturedActivities } from './VActivityFeaturedActivities';
import { V_TABS } from './VActivityPage.constants';
import {
    formatList,
    formatProductOptions,
    formatDuration,
    getMinPrice,
} from '../app/utils/mapVProductToTabs';
import { getVContent } from '../app/utils/getVContent';
import VActivityReviews from './VActivityReviews';
import { fetchViatorReviews } from '../service/fetchViatorReviews';
import { trackViatorClick } from '@/shared/Analytics/GA4';
import {
    addCampaignToUrl,
    CAMPAIGN_SESSION_STORAGE_KEY,
    resolveCampaign,
} from '../app/utils/campaign';

const upgradeToDirectRedirect = (link: string) =>
    link.includes('&') ? link.concat('&target_lander=NONE') : link;

const VActivityPageContainer = ({
    product,
    breadcrumbs,
    currentDestination,
    recommends,
    reviews: initialReviews = [],
    destinationId,
}: VActivityPageProps): ReactElement => {
    useState(false);
    const [indexOpen, setIndexOpen] = useState<number | null>(0);
    const [reviews, setReviews] = useState<IReview[]>(initialReviews || []);

    useEffect(() => {
        if (!initialReviews?.length) {
            const productCode = product.id;
            fetchViatorReviews(productCode)
                .then((data) => setReviews(data))
                .catch((error) => console.error('Failed to fetch reviews:', error));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product.id]);

    const tabData = useMemo(() => {
        return {
            inclusions: formatList(product.product_features.inclusions),
            exclusions: formatList(product.product_features.exclusions),
            additionalInfo: formatList(product.product_features.highlights),
            cancellationPolicy: product.ui.canceletionPolicyDescription,
            itinerary: formatList(product.ui.itineraryDescriptions),
            productOptions: formatProductOptions(product.options),
        };
    }, [product]);

    const tabs = V_TABS.map(({ title, key, id }) => getVContent(title, tabData, key, id)).filter(
        (item) => item !== undefined
    ) as IAccordionTab[];

    const handleClickOnTab = (index: number) => {
        if (index === indexOpen) {
            setIndexOpen(null);
        } else {
            setIndexOpen(index);
        }
    };

    const baseProductUrl = upgradeToDirectRedirect(product.options[0].landing_page);
    const [productUrl, setProductUrl] = useState(baseProductUrl);

    useEffect(() => {
        const campaign = resolveCampaign({
            search: window.location.search,
            referrer: document.referrer,
            currentHostname: window.location.hostname,
            storedCampaign: sessionStorage.getItem(CAMPAIGN_SESSION_STORAGE_KEY),
        });

        if (campaign) {
            sessionStorage.setItem(CAMPAIGN_SESSION_STORAGE_KEY, campaign);
        }

        setProductUrl(addCampaignToUrl(baseProductUrl, campaign));
    }, [baseProductUrl]);

    const handleBookNow = () => {
        if (typeof window !== 'undefined') {
            trackViatorClick({
                product_id: product.id,
                destination_id: destinationId || '',
            });

            window.open(productUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const formattedDuration = formatDuration(product.options);
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const featuredTitle = currentDestination
        ? `Similar Activities in ${currentDestination.name}`
        : 'Recommended Activities';

    const images = product.related_media.map((i) => ({ id: '', slug: '', url: i.url }));
    const { price: minPrice, currency } = getMinPrice(product.options);

    return (
        <Layout breadcrumbs={breadcrumbs}>
            <VActivityMeta product={product} url={currentUrl} imageUrl={images[0]?.url} />
            <div data-test-id="VDetails">
                <Container>
                    <h1
                        className="tx-heading-2 m-0 w-full pb-4 text-black-normal lg:pb-6 dark:text-white"
                        data-test-id="VDetails_title"
                    >
                        {product.title}
                    </h1>
                    <VActivitySubtitle
                        destination={
                            currentDestination
                                ? {
                                      name: currentDestination.name,
                                      code: currentDestination.state.code,
                                  }
                                : undefined
                        }
                        totalReviews={product.rating.rating_count}
                        rating={product.rating.average_value}
                        handleBookNow={handleBookNow}
                    />
                    <VActivityDetail
                        images={images}
                        productUrl={productUrl}
                        onBookNow={handleBookNow}
                        price={minPrice}
                        currency={currency}
                    />
                    {product.ui.supplierName && (
                        <S.Partner data-test-id="VDetails_partner_link">
                            By: &nbsp;
                            <span>{product.ui.supplierName}</span>
                        </S.Partner>
                    )}
                    {product.description && (
                        <S.ActivityDescription>
                            <Paragraph
                                id="v_activity_paragraph"
                                as="div"
                                innerHtml={product.description}
                            />
                            {formattedDuration && (
                                <S.Duration>
                                    <Tomorrow />
                                    Duration {formattedDuration}
                                </S.Duration>
                            )}
                        </S.ActivityDescription>
                    )}

                    <VActivityTabs
                        handleClickOnTab={handleClickOnTab}
                        indexOpen={indexOpen}
                        tabs={tabs}
                    />
                </Container>
            </div>
            <VActivityFeaturedActivities
                recommends={recommends}
                currentDestination={currentDestination}
                title={featuredTitle}
                isLoading={false}
            />
            <VActivityReviews reviews={reviews || []} showMore={handleBookNow} />
            <VStickyFooter
                totalReviews={product.rating.rating_count}
                rating={product.rating.average_value}
                onBookNow={handleBookNow}
                price={minPrice}
                currency={currency}
            />
        </Layout>
    );
};

export const VActivityPage = memo(VActivityPageContainer);
